import { FontMetrics } from "../util/font";
import { TextAlgorithm, TextboxElement } from "../types";
import { refitTextbox } from "./algorithm/traditional";

/**
 * The size of a step used to compute font sizes.
 */
export const fontSizeStep = 1;

/**
 * Computes the width of text. See the following link for a detailed breakdown.
 * https://developer.tizen.org/community/tip-tech/working-fonts-using-opentype.js?langswitch=en
 * @param text The text to get width for.
 * @param scale The scale
 * @param cache The font metric cache to be used for calculations.
 * @returns The width of the text.
 */
export const textWidth = (text: string, scale: number, cache: FontMetrics): number => {
    let width = 0;
    const glyphs = cache.getGlyphs(text);

    glyphs.forEach((glyph, idx) => {
        if (glyph.advanceWidth) {
            width += glyph.advanceWidth;
        }
        if (idx < glyphs.length - 1) {
            const kerningValue = cache.getKerningValue(glyph, glyphs[idx + 1]);
            width += kerningValue || 0;
        }
    });
    width *= scale;
    return width;
};

/**
 * Computes the width of the larges individual character in the input text.
 * @param text The input text.
 * @param scale The scale of the text.
 * @param cache The font metric cache to be used for calculations.
 * @returns The width of the largest character.
 */
export const charWidth = (text: string, scale: number, cache: FontMetrics): number => {
    let maxWidth = 0;
    const glyphs = cache.getGlyphs(text);

    glyphs.forEach((glyph, idx) => {
        if (glyph.advanceWidth) {
            maxWidth = Math.max(maxWidth, glyph.advanceWidth);
        }
        if (idx < glyphs.length - 1) {
            const kerningValue = cache.getKerningValue(glyph, glyphs[idx + 1]);
            maxWidth = Math.max(maxWidth, kerningValue || 0);
        }
    });
    maxWidth *= scale;
    return maxWidth;
};

/**
 * Computes the width of the largest line.
 * @param lines The input text split by line.
 * @param fontScale The scale of the text.
 * @param cache The font metric cache to be used for calculations.
 * @returns The width of the largest line.
 */
export const lineWidth = (lines: string[], fontScale: number, cache: FontMetrics): number => {
    let maxWidth = 0;
    lines.forEach((line) => {
        maxWidth = Math.max(maxWidth, textWidth(line, fontScale, cache));
    });
    return maxWidth;
};

interface TextTransformationConfiguration {
    stripControlCharacters: boolean;
    vertical: boolean;
    uppercase: boolean;
}

const transformationDefaults: TextTransformationConfiguration = {
    stripControlCharacters: true,
    vertical: false,
    uppercase: false,
};

/**
 * Applies a number of different transformations to a piece of text based on configuration
 * @param text The text to transform.
 * @param options The requested options, this will default to sensible defaults if not provided.
 * @returns A string represented the transformed value of the input text.
 */
export const applyTextTransformations = (
    text: string,
    options: Partial<TextTransformationConfiguration> = transformationDefaults,
) => {
    // We apply our own defaults to any fields that aren't supplied.
    const configuration = { ...transformationDefaults, ...options };

    let result = text || "";

    // Control characters are not valid in XML, we strip them from any input to
    // ensure that invalid data doesn't enter the SVG generated by our system.
    // \u000A and \u000D are carriage return and newline sequences, we want to keep these.
    // eslint-disable-next-line no-control-regex
    if (configuration.stripControlCharacters) {
        result = result.replace(/^(?![\u000A\u000D])[\u0000-\u001F\u007F-\u009F]/g, "");
    }

    // Force upper case
    if (configuration.uppercase) {
        result = result.toUpperCase();
    }

    // Inserts newlines into text to make a vertical string
    if (configuration.vertical) {
        result = result.split("").join("\n");
    }

    return result;
};

// https://stackoverflow.com/a/22015771
// Helpful function for zipping two arrays together
export const zip = (xs: any[], ys: any[]) => {
    return xs.map((x, idx) => [x, ys[idx]]);
};

export const getTextAlignment = (align: string = "center", isVertical: boolean = false) => {
    if (isVertical) {
        return "center";
    }
    return align;
};

/**
 * Returns the correct text anchor for the given alignment.
 */
export const getAnchor = (align?: string) => {
    if (align === "left") {
        return "start";
    }
    if (align === "right") {
        return "end";
    }
    return "middle";
};

export const recomputeTextOnElement = (el: TextboxElement, newText: string, oldEl?: TextboxElement): TextboxElement => {
    if (!el.fontData) {
        return el;
    }
    const getCalculatedMixins = () => {
        // TODO: Pull the autosize algorithm in here as well. This means all sizing
        // will occur in one place. Any command that relies on text changes that could affect
        // the textbox size in any way should pass through this function.
        if (el.algorithm === TextAlgorithm.Traditional) {
            const calculatedText = refitTextbox(newText, el, oldEl, el.lineHeight);
            return {
                text: calculatedText.lines.join("\n"),
                height: calculatedText.requiredHeight,
                width: calculatedText.requiredWidth,
                fontSize: calculatedText.fontSize,
                input: newText,
            };
        } else {
            return {
                text: newText,
            };
        }
    };
    return {
        ...el,
        ...getCalculatedMixins(),
    };
};

/**
 * The default line height to use in situations where none is specified.
 * Typical default is suggested to be between 1.1 & 1.2
 */
export const defaultLineHeightFactor = 1.1;
